import { useStyles, Image } from '@archipro-design/aria';
import * as S from './DirectoryPotmTile.style';
import { useRef, useState, useEffect } from 'react';
import { useIsWebmLegacyDevice } from '@modules/root/hook/use-is-webm-legacy-device';
import { useInViewport } from 'ahooks';
import { Link } from '@remix-run/react';
import { useHydrated } from 'remix-utils';
import FilePlayer from 'react-player/file';
import {
    useAdEventHandler,
    useLogImpression,
    useTracker,
} from '@archipro-website/tracker';
import type { AdvertCreative } from '~/modules/adverts/type';
import { createLinkedComponentParams } from '~/utils/urlHelper';

interface PotmTileProps {
    advertCreative: AdvertCreative;
}

const LEGACY_URLS = [
    '/home-design-event',
    '/building-guide',
    '/box-the-architect-builder',
    '/advertising',
    '/careers',
    '/contact-us',
];

const AdvertPotmTileMobile = ({ advertCreative }: PotmTileProps) => {
    const id = advertCreative.id;
    const title = advertCreative.title;
    const link = advertCreative.cta.href;
    const professionalID = advertCreative.professional.id;
    const videoCover = advertCreative.video?.coverMobile || '';
    const videoWebm = advertCreative.video?.webmMobile || '';
    const videoMp4 = advertCreative.video?.mp4Mobile || '';
    const autoplay = !!advertCreative.video?.autoplayMobile;

    const tileWidth = 437;
    const tileHeight = 761;
    const [playing, setPlaying] = useState(false);
    const [videoSources, setVideoSources] = useState([videoWebm, videoMp4]);
    const hydrated = useHydrated();
    const { css } = useStyles({ tileWidth, tileHeight, playing });
    const ref = useRef<HTMLAnchorElement>(null);
    const isWebmLegacyDevice = useIsWebmLegacyDevice();
    const [inViewport] = useInViewport(ref, {
        threshold: 0.2,
    });

    useEffect(() => {
        setVideoSources(
            isWebmLegacyDevice ? [videoMp4, videoWebm] : [videoWebm, videoMp4],
        );
    }, [videoMp4, videoWebm, isWebmLegacyDevice]);

    const tracker = useTracker();
    useLogImpression({
        data: {
            professionalID: Number(professionalID),
            itemID: Number(id),
            type: 'VerticalTile_Advert',
        },
        ref,
    });

    const { onClick } = useAdEventHandler(advertCreative, ref);
    const clickType =
        (videoMp4 || videoWebm) && autoplay ? 'Ad_VideoClick' : 'Ad_ImageClick';
    const { onClick: trackClick } = createLinkedComponentParams(
        tracker,
        clickType,
        advertCreative.cta.href,
        {
            libraryItemID: advertCreative.professional.id,
            professionalID: advertCreative.professional.id,
            professionalTitle: advertCreative.professional.title,
        },
        null,
        onClick,
    );

    //server render only cover image
    const video =
        hydrated && videoSources.length > 0 ? (
            <div className={css(S.PlayerMobile)}>
                <FilePlayer
                    url={videoSources}
                    playing={autoplay && inViewport}
                    muted={true}
                    loop={true}
                    controls={false}
                    playsinline={true}
                    width={'100%'}
                    height={'100%'}
                    onPlay={() => setPlaying(true)}
                    config={{
                        attributes: {
                            preload: 'none',
                            poster: videoCover,
                        },
                    }}
                />
            </div>
        ) : null;

    return LEGACY_URLS.includes(link) ? (
        <a
            href={link || '#'}
            ref={ref}
            style={{
                width: '300px',
                height: '300px',
            }}
            className={css(S.DirectoryGridContainerMobile)}
            onClick={trackClick}
        >
            <div className={css(S.VideoAspectMobile)}>
                {video}
                <Image
                    className={css(S.CoverMobile)}
                    src={videoCover}
                    width={300}
                    height={300}
                    layout={'intrinsic'}
                    objectFit={'cover'}
                    objectPosition={'center'}
                    alt={title || ''}
                    styles={{ width: '100%' }}
                />
            </div>
        </a>
    ) : (
        <Link
            to={link || '#'}
            ref={ref}
            className={css(S.DirectoryGridContainerMobile)}
            prefetch="intent"
            onClick={trackClick}
        >
            <div className={css(S.VideoAspectMobile)}>
                {video}
                <Image
                    className={css(S.CoverMobile)}
                    src={videoCover}
                    width={300}
                    height={300}
                    layout={'intrinsic'}
                    objectFit={'cover'}
                    objectPosition={'center'}
                    alt={title || ''}
                    styles={{ width: '100%' }}
                />
            </div>
        </Link>
    );
};

export default AdvertPotmTileMobile;
